import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Statistic, Spin, Tag, Table, Progress, Timeline, Divider, Button } from 'antd';
import { DatabaseOutlined, CloudServerOutlined, CheckCircleOutlined, ClockCircleOutlined, HddOutlined, ReloadOutlined, PrinterOutlined, FolderOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;

export default function BackupStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/performance/backup-status');
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Backup Status Report</title>
      <style>body{font-family:Arial;padding:30px}.header{background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:20px 30px;border-radius:12px;margin-bottom:20px}
      .header h1{margin:0;font-size:22px}table{width:100%;border-collapse:collapse;margin:16px 0}td,th{border:1px solid #e8e8e8;padding:8px 12px;text-align:left;font-size:13px}th{background:#f0f5ff}
      .section{margin:20px 0}.section h3{border-bottom:2px solid #667eea;padding-bottom:8px}.footer{text-align:center;color:#999;font-size:12px;margin-top:30px;border-top:1px solid #eee;padding-top:10px}</style></head><body>
      <div class="header"><h1>Data & Backup Status Report</h1><p>Bainsla Music Private Limited | ${dayjs().format('DD MMM YYYY, hh:mm A')}</p></div>
      <div class="section"><h3>Database</h3><p>Size: <b>${data?.db_size_mb || 0} MB</b> | Uploads: <b>${data?.uploads_size_mb || 0} MB</b> | Total: <b>${((data?.db_size_mb || 0) + (data?.uploads_size_mb || 0)).toFixed(1)} MB</b></p></div>
      <div class="section"><h3>Last Backup</h3><p>${data?.last_backup ? dayjs(data.last_backup).format('DD MMM YYYY, hh:mm A') : 'No backup found'} | Size: ${data?.last_backup_size_mb || 0} MB</p>
      <p>Next Backup: ${data?.next_backup ? dayjs(data.next_backup).format('DD MMM YYYY, hh:mm A') : '-'}</p></div>
      <div class="section"><h3>Table Records</h3><table><tr><th>Table</th><th>Records</th></tr>
      ${Object.entries(data?.table_counts || {}).map(([n, c]) => `<tr><td>${n}</td><td>${c.toLocaleString()}</td></tr>`).join('')}
      </table></div>
      ${(data?.backup_history || []).length > 0 ? `<div class="section"><h3>Backup History</h3><table><tr><th>File</th><th>Date</th><th>Size</th></tr>
      ${data.backup_history.map(b => `<tr><td>${b.file}</td><td>${dayjs(b.date).format('DD MMM YYYY, hh:mm A')}</td><td>${b.size_mb} MB</td></tr>`).join('')}
      </table></div>` : ''}
      <div class="footer">Bainsla Music Data Dashboard | Generated on ${dayjs().format('DD MMM YYYY, hh:mm A')}</div></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  const tableColumns = [
    { title: 'Table', dataIndex: 'name', render: v => <Text strong>{v}</Text> },
    { title: 'Records', dataIndex: 'count', render: v => <Tag color="blue">{v.toLocaleString()}</Tag> }
  ];

  const tableData = data ? Object.entries(data.table_counts).map(([name, count]) => ({ name, count, key: name })) : [];

  const tableSizeColumns = [
    { title: 'Table', dataIndex: 'table_name', render: v => <Text strong style={{ fontSize: 12 }}>{v}</Text> },
    { title: 'Rows', dataIndex: 'table_rows', width: 80, render: v => <Tag color="blue">{(v || 0).toLocaleString()}</Tag> },
    { title: 'Size (MB)', dataIndex: 'size_mb', width: 90, render: v => <Text>{v}</Text> },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col><Title level={3}><DatabaseOutlined /> Data & Backup Status</Title></Col>
        <Col>
          <Row gutter={8}>
            <Col><Button icon={<ReloadOutlined />} onClick={fetchData}>Refresh</Button></Col>
            <Col><Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>Download PDF</Button></Col>
          </Row>
        </Col>
      </Row>
      <Text type="secondary">Real-time database size, backup history, and storage information</Text>

      {loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} /> : data ? (
        <>
          {/* Server Disk Space */}
          {data.disk_total_gb > 0 && (
            <Card size="small" style={{ marginTop: 16, background: data.disk_percent >= 90 ? '#fff2f0' : data.disk_percent >= 75 ? '#fffbe6' : '#f6ffed', borderLeft: `4px solid ${data.disk_percent >= 90 ? '#ff4d4f' : data.disk_percent >= 75 ? '#faad14' : '#52c41a'}` }}>
              <Row gutter={16} align="middle">
                <Col xs={24} sm={8}>
                  <Statistic
                    title={<span><CloudServerOutlined /> Server Disk Space</span>}
                    value={data.disk_used_gb}
                    suffix={`/ ${data.disk_total_gb} GB Used`}
                    valueStyle={{ color: data.disk_percent >= 90 ? '#ff4d4f' : data.disk_percent >= 75 ? '#faad14' : '#52c41a', fontSize: 22 }}
                  />
                </Col>
                <Col xs={24} sm={10}>
                  <Progress
                    percent={data.disk_percent}
                    strokeColor={data.disk_percent >= 90 ? '#ff4d4f' : data.disk_percent >= 75 ? '#faad14' : '#52c41a'}
                    format={pct => `${pct}%`}
                    size="default"
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {data.disk_free_gb} GB Free | {data.disk_total_gb} GB Total
                  </Text>
                </Col>
                <Col xs={24} sm={6}>
                  <Tag color={data.disk_percent >= 90 ? 'red' : data.disk_percent >= 75 ? 'orange' : 'green'} style={{ fontSize: 14, padding: '4px 12px' }}>
                    {data.disk_percent >= 90 ? '⚠️ Critical' : data.disk_percent >= 75 ? '⚠️ Warning' : '✓ Healthy'}
                  </Tag>
                </Col>
              </Row>
            </Card>
          )}

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={12} sm={6}>
              <Card><Statistic title="Database Size" value={data.db_size_mb} suffix="MB" prefix={<DatabaseOutlined />} valueStyle={{ color: '#667eea' }} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card><Statistic title="Uploads Size" value={data.uploads_size_mb} suffix="MB" prefix={<HddOutlined />} valueStyle={{ color: '#722ed1' }} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Last Backup"
                  value={data.last_backup ? dayjs(data.last_backup).format('DD MMM, hh:mm A') : 'No backup found'}
                  prefix={data.last_backup ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                  valueStyle={{ color: data.last_backup ? '#52c41a' : '#faad14', fontSize: 16 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="Next Backup" value={data.next_backup ? dayjs(data.next_backup).format('DD MMM, hh:mm A') : '-'}
                  prefix={<ClockCircleOutlined />} valueStyle={{ color: '#1890ff', fontSize: 16 }} />
              </Card>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <Card title="Table Records" size="small">
                <Table columns={tableColumns} dataSource={tableData} size="small" pagination={false} />
              </Card>

              {(data.backup_history || []).length > 0 && (
                <Card title="Backup History" size="small" style={{ marginTop: 16 }}>
                  <Timeline>
                    {data.backup_history.map((b, i) => (
                      <Timeline.Item key={i} color={i === 0 ? 'green' : 'blue'} dot={i === 0 ? <CheckCircleOutlined /> : undefined}>
                        <Text strong style={{ fontSize: 13 }}>{b.file}</Text><br />
                        <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(b.date).format('DD MMM YYYY, hh:mm A')} | {b.size_mb} MB</Text>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </Card>
              )}
            </Col>
            <Col xs={24} md={12}>
              <Card title="Storage Breakdown" size="small">
                {tableData.sort((a, b) => b.count - a.count).map(t => {
                  const maxCount = Math.max(...tableData.map(r => r.count));
                  const pct = maxCount > 0 ? Math.round((t.count / maxCount) * 100) : 0;
                  return (
                    <div key={t.name} style={{ marginBottom: 8 }}>
                      <Row justify="space-between">
                        <Text style={{ fontSize: 13 }}>{t.name}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>{t.count.toLocaleString()} records</Text>
                      </Row>
                      <Progress percent={pct} showInfo={false} strokeColor="#667eea" size="small" />
                    </div>
                  );
                })}
              </Card>

              {Object.keys(data.upload_breakdown || {}).length > 0 && (
                <Card title={<span><FolderOutlined /> Uploads Breakdown</span>} size="small" style={{ marginTop: 16 }}>
                  {Object.entries(data.upload_breakdown).map(([folder, sizeMb]) => (
                    <div key={folder} style={{ marginBottom: 6 }}>
                      <Row justify="space-between">
                        <Text style={{ fontSize: 13 }}><FolderOutlined style={{ marginRight: 4 }} />{folder}</Text>
                        <Text type="secondary">{sizeMb} MB</Text>
                      </Row>
                      <Progress percent={data.uploads_size_mb > 0 ? Math.round((sizeMb / data.uploads_size_mb) * 100) : 0} showInfo={false} strokeColor="#722ed1" size="small" />
                    </div>
                  ))}
                </Card>
              )}

              {(data.uploads_backups || []).length > 0 && (
                <Card title="Uploads Backup History" size="small" style={{ marginTop: 16 }}>
                  <Timeline>
                    {data.uploads_backups.map((b, i) => (
                      <Timeline.Item key={i} color={i === 0 ? 'green' : 'gray'}>
                        <Text strong style={{ fontSize: 13 }}>{b.file}</Text><br />
                        <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(b.date).format('DD MMM YYYY, hh:mm A')} | {b.size_gb} GB</Text>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </Card>
              )}

              {(data.table_sizes || []).length > 0 && (
                <Card title="Table Sizes" size="small" style={{ marginTop: 16 }}>
                  <Table columns={tableSizeColumns} dataSource={data.table_sizes} rowKey="table_name" size="small" pagination={false} />
                </Card>
              )}

              <Card title="Server Info" size="small" style={{ marginTop: 16 }}>
                <Row><Col span={12}><Text type="secondary">Server Time:</Text></Col><Col span={12}><Text strong>{data.server_time}</Text></Col></Row>
                <Row style={{ marginTop: 8 }}><Col span={12}><Text type="secondary">Total Storage:</Text></Col><Col span={12}><Text strong>{(data.db_size_mb + data.uploads_size_mb).toFixed(1)} MB</Text></Col></Row>
                <Row style={{ marginTop: 8 }}><Col span={12}><Text type="secondary">Backup Size:</Text></Col><Col span={12}><Text strong>{data.last_backup_size_mb || 0} MB</Text></Col></Row>
                {data.disk_total_gb > 0 && <>
                  <Divider style={{ margin: '8px 0' }} />
                  <Row style={{ marginTop: 4 }}><Col span={12}><Text type="secondary">Disk Total:</Text></Col><Col span={12}><Text strong>{data.disk_total_gb} GB</Text></Col></Row>
                  <Row style={{ marginTop: 4 }}><Col span={12}><Text type="secondary">Disk Used:</Text></Col><Col span={12}><Text strong style={{ color: data.disk_percent >= 90 ? '#ff4d4f' : undefined }}>{data.disk_used_gb} GB ({data.disk_percent}%)</Text></Col></Row>
                  <Row style={{ marginTop: 4 }}><Col span={12}><Text type="secondary">Disk Free:</Text></Col><Col span={12}><Text strong style={{ color: '#52c41a' }}>{data.disk_free_gb} GB</Text></Col></Row>
                </>}
              </Card>
            </Col>
          </Row>
        </>
      ) : null}
    </div>
  );
}
